import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
const NONE = '__none__';
const norm = (s)=>s.trim().toLowerCase();
const label = 'text-[length:var(--fs-ui-label)] font-medium uppercase tracking-[var(--ls-ui-eyebrow)] text-muted-foreground';
// Likely existing = same title or one contains the other. "Similar" is only a
// prompt to look: the reviewer decides whether it is the SAME definition
// (occupational equivalence is Rosetta's job, not this panel's).
const isLikely = (title, e)=>{
    const a = norm(title);
    const b = norm(e.title);
    return !!a && !!b && (a === b || a.includes(b) || b.includes(a));
};
/**
 * C2-06/C2-07: correct the wording while looking at the source, then accept it
 * as a new definition, resolve it to a specific existing definition+version, or
 * reject it. The candidate's original proposal is never edited — the server
 * keeps it and records this correction beside it.
 */ export function CandidateDecisionPanel({ candidate, entries, companies, vehicles, canEdit, busy, onAccept, onReject }) {
    const [title, setTitle] = useState(candidate.title);
    const [description, setDescription] = useState(candidate.description);
    const [qualifications, setQualifications] = useState(candidate.qualifications);
    const [companyId, setCompanyId] = useState(candidate.companyProfileId);
    const [vehicleId, setVehicleId] = useState(candidate.contractVehicleId);
    const [ack, setAck] = useState(false);
    const edited = title !== candidate.title || description !== candidate.description || qualifications !== candidate.qualifications || companyId !== candidate.companyProfileId || vehicleId !== candidate.contractVehicleId;
    const correction = {
        title,
        description,
        qualifications,
        companyProfileId: companyId,
        contractVehicleId: vehicleId
    };
    const likely = entries.filter((e)=>isLikely(title, e));
    const conflicts = (e)=>!!norm(qualifications) && !!norm(e.qualifications) && norm(qualifications) !== norm(e.qualifications);
    const anyConflict = likely.some(conflicts);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: label,
                        children: "Correct before deciding"
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Title",
                        value: title,
                        disabled: !canEdit,
                        "data-testid": Testids.deliveryRegistries.lcatCandidateCorrectTitle,
                        onChange: (e)=>setTitle(e.target.value)
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        "aria-label": "Definition",
                        rows: 3,
                        value: description,
                        disabled: !canEdit,
                        "data-testid": Testids.deliveryRegistries.lcatCandidateCorrectDescription,
                        onChange: (e)=>setDescription(e.target.value)
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        "aria-label": "Qualifications",
                        rows: 2,
                        value: qualifications,
                        disabled: !canEdit,
                        "data-testid": Testids.deliveryRegistries.lcatCandidateCorrectQualifications,
                        onChange: (e)=>setQualifications(e.target.value)
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-2 gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs(Select, {
                                value: companyId || NONE,
                                onValueChange: (v)=>setCompanyId(v === NONE ? '' : v),
                                disabled: !canEdit,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "aria-label": "Company",
                                        "data-testid": Testids.deliveryRegistries.lcatCandidateCorrectCompany,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: NONE,
                                                children: "No company"
                                            }),
                                            companies.map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: c.id,
                                                    children: c.name
                                                }, c.id))
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: vehicleId || NONE,
                                onValueChange: (v)=>setVehicleId(v === NONE ? '' : v),
                                disabled: !canEdit,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "aria-label": "Vehicle",
                                        "data-testid": Testids.deliveryRegistries.lcatCandidateCorrectVehicle,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: NONE,
                                                children: "No vehicle"
                                            }),
                                            vehicles.map((v)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: v.id,
                                                    children: v.name
                                                }, v.id))
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                "data-testid": Testids.deliveryRegistries.lcatCandidateInspectMatches,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: label,
                        children: "Likely existing definitions"
                    }),
                    likely.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                        children: "No existing catalog entries share this title."
                    }),
                    anyConflict && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "warning",
                        "data-testid": Testids.deliveryRegistries.lcatCandidateConflictWarning,
                        children: "Qualifications differ from at least one existing definition below"
                    }),
                    likely.map((e)=>/*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1 rounded-md border border-border p-2 text-[length:var(--fs-ui-body)]",
                            "data-testid": Testids.deliveryRegistries.lcatCandidateMatchRow,
                            "data-entry-id": e.id,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "font-medium",
                                    children: [
                                        e.title,
                                        " · v",
                                        e.version
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "whitespace-pre-wrap text-muted-foreground",
                                    children: e.description || 'No definition text'
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "text-muted-foreground",
                                    children: [
                                        "Qualifications: ",
                                        e.qualifications || 'none stated'
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "text-muted-foreground",
                                    children: [
                                        e.companyName || 'No company',
                                        " · ",
                                        e.contractVehicleName || 'No vehicle',
                                        e.zouProjectId ? ` · source ${e.zouProjectId}` : ''
                                    ]
                                }),
                                canEdit && /*#__PURE__*/ _jsxs(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    disabled: busy || conflicts(e) && !ack,
                                    "data-testid": Testids.deliveryRegistries.lcatCandidateResolve,
                                    onClick: ()=>onAccept({
                                            id: candidate.id,
                                            correction,
                                            resolveToEntryId: e.id,
                                            acknowledgeConflict: conflicts(e) && ack
                                        }),
                                    children: [
                                        "Resolve to this definition (v",
                                        e.version,
                                        ")"
                                    ]
                                })
                            ]
                        }, e.id)),
                    anyConflict && canEdit && /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                        className: "cursor-pointer items-center text-xs text-muted-foreground",
                        "data-testid": Testids.deliveryRegistries.lcatCandidateConflictAck,
                        label: "I have compared the qualifications and still want to resolve to the existing definition",
                        controlProps: {
                            checked: ack,
                            onCheckedChange: (v)=>setAck(v === true)
                        }
                    })
                ]
            }),
            canEdit && /*#__PURE__*/ _jsxs("div", {
                className: "flex justify-end gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        disabled: busy || !title.trim(),
                        "data-testid": Testids.deliveryRegistries.lcatCandidateInspectAccept,
                        onClick: ()=>onAccept(edited ? {
                                id: candidate.id,
                                correction
                            } : candidate.id),
                        children: edited ? 'Accept as new definition (corrected)' : 'Accept as new definition'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "destructive",
                        disabled: busy,
                        "data-testid": Testids.deliveryRegistries.lcatCandidateInspectReject,
                        onClick: ()=>onReject(candidate.id),
                        children: "Reject candidate"
                    })
                ]
            })
        ]
    });
}
